import React, { createContext, useState, useCallback, useEffect } from "react";
import { 
  getRecruitments, 
  getRecruitmentById as getRecruitmentByIdApi,
  createRecruitment as createRecruitmentService,
  updateRecruitment as updateRecruitmentService,
  deleteRecruitment as deleteRecruitmentService,
  getRecruitmentSubmissions,
  createRecruitmentSubmission,
  deleteRecruitmentSubmission,
  getCandidates
} from "../../Services/recruiterapis";
import LOCAL_KEYS, { parseLocalStorageJSON } from "../../helpers/localstorage";

export const RecruiterContext = createContext();

export const RecruiterProvider = ({ children }) => {
  const [recruitments, setRecruitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecruitments, setTotalRecruitments] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [submissions, setSubmissions] = useState([]);
  const [candidates, setCandidates] = useState([]);

  const fetchRecruitments = async (filters = {}) => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setLoading(false);
      return { content: [], totalElements: 0, totalPages: 0 };
    }

    setLoading(true);
    try {
      const data = await getRecruitments(filters);
      setRecruitments(data.content || []);
      setTotalRecruitments(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      return data;
    } catch (error) {
      let backendMsg = error?.response?.data?.message || error?.message || "Failed to fetch recruitments";
      setError(backendMsg);
      setRecruitments([]);
      setTotalRecruitments(0);
      setTotalPages(0);
      throw new Error(backendMsg);
    } finally {
      setLoading(false);
    }
  };

  const getRecruitmentById = useCallback(async (id) => {
    try {
      const recruitment = await getRecruitmentByIdApi(id);
      return recruitment;
    } catch (error) {
      throw error;
    }
  }, []);

  const addRecruitment = async (recruitmentData) => {
    try {
      await createRecruitmentService(recruitmentData);
      await fetchRecruitments(); // Refresh the list after adding
    } catch (error) {
      console.error("Failed to add recruitment:", error);
      throw error;
    }
  };

  const updateRecruitment = async (id, recruitmentData) => {
    try {
      await updateRecruitmentService(id, recruitmentData);
      await fetchRecruitments(); // Refresh the list after updating
    } catch (error) {
      console.error("Failed to update recruitment:", error);
      throw error;
    }
  };

  const removeRecruitment = async (id) => {
    try {
      await deleteRecruitmentService(id);
      await fetchRecruitments(); // Refresh the list after deleting
    } catch (error) {
      console.error("Failed to delete recruitment:", error);
      throw error;
    }
  };

  const fetchRecruitmentSubmissions = async (id, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRecruitmentSubmissions(id, filters);
      setSubmissions(data.content || []);
      return data;
    } catch (err) {
      // Extract error message from various possible locations
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          (typeof err?.response?.data === 'string' ? err.response.data : null) ||
                          err?.message || 
                          "Failed to load submissions.";
      setError(errorMessage);
      setSubmissions([]);
      // Throw the error so components can catch it and show toast
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addRecruitmentSubmission = async (id, payload) => {
    try {
      await createRecruitmentSubmission(id, payload);
      // Try to refresh submissions, but don't fail if refresh fails
      try {
        await fetchRecruitmentSubmissions(id);
      } catch (refreshError) {
        console.warn("Submission added but failed to refresh list:", refreshError);
        // Don't throw - the add was successful
      }
    } catch (error) {
      console.error("Failed to add submission:", error);
      throw error;
    }
  };

  const removeRecruitmentSubmission = async (id, submissionId) => {
    try {
      await deleteRecruitmentSubmission(id, submissionId);
      // Try to refresh submissions, but don't fail if refresh fails
      try {
        await fetchRecruitmentSubmissions(id);
      } catch (refreshError) {
        console.warn("Submission removed but failed to refresh list:", refreshError);
        // Don't throw - the remove was successful
      }
    } catch (error) {
      console.error("Failed to delete submission:", error);
      throw error;
    }
  };

  const fetchCandidates = async () => {
    try {
      const data = await getCandidates();
      setCandidates(data || []);
      return data;
    } catch (error) {
      console.error("Failed to fetch candidates:", error);
      setCandidates([]);
      return [];
    }
  };

  useEffect(() => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      fetchRecruitments({ stsCd: "A" }); // Only fetch active recruitments by default
    }
  }, []);

  return (
    <RecruiterContext.Provider 
      value={{ 
        recruitments, 
        loading, 
        fetchRecruitments, 
        addRecruitment,
        getRecruitmentById,
        updateRecruitment,
        removeRecruitment,
        error,
        totalRecruitments,
        totalPages,
        submissions,
        fetchRecruitmentSubmissions,
        addRecruitmentSubmission,
        removeRecruitmentSubmission,
        candidates,
        fetchCandidates
      }}
    >
      {children}
    </RecruiterContext.Provider>
  );
};

